import { useEffect, useMemo, useRef } from "react";
import { Link } from "react-router-dom";
import {
  Bell,
  Calendar,
  ChevronRight,
  CircleHelp,
  ClipboardList,
  Heart,
  Info,
  Users,
  type LucideIcon,
} from "lucide-react";
import { useShellProfile } from "../../state/useShellProfile";
import {
  useFinancialAidByApplicant,
  useFinancialAidOpenCycle,
} from "../../financial-aid/hooks/useFinancialAidApi";
import { resolveFinancialAidOpenCycleAcademicYear } from "../../financial-aid/financialAidAcademicYear";
import { resolveCurrentParentFinancialAidApplicationSummary } from "../../financial-aid/parentFinancialAidApplicationSummary";
import { parseSettingsState } from "../../settings/settingsState";
import {
  materializeModuleGovernanceState,
  resolveEnabledParentSidebarNavIds,
} from "../../platform-governance/moduleGovernanceCatalog";
import {
  isParentPortalModuleVisible,
  resolveEffectiveParentPortalNavIds,
} from "../parentPortalModuleAvailability";
import {
  useGetSettingsModuleConfigQuery,
  useListNotificationsQuery,
  useListPortalCalendarEventsQuery,
  useListPortalResourcesQuery,
  useListPortalStudentProfilesQuery,
  useMarkAllNotificationsReadMutation,
} from "../../state/domainRouterApi";
import { SupportErrorMessage } from "../../components/overlays/EngineeringSupportBanner";

type DashboardNotification = {
  id: string;
  message: string;
  unread: boolean;
  createdAtMs: number;
};

type StudentScheduleEntry = {
  id: string;
  atMs: number;
  timeLabel: string;
  subject: string;
};

type StudentCard = {
  id: string;
  firstName: string;
  lastName: string;
  gradeToken: string;
  gradeLabel: string;
  avatarInitial: string;
  scheduleEntries: StudentScheduleEntry[];
};

type ParnasSnapshot = {
  sponsorFamilyName: string;
  dedicationText: string;
  sponsorUrl: string;
  hebrewDateLabel: string;
};

type ResourceCardDescriptor = {
  key: string;
  title: string;
  subtitle: string;
  icon: LucideIcon;
  iconColor: string;
  iconBg: string;
};

type ResourceCard = ResourceCardDescriptor & {
  href: string;
  isAvailable: boolean;
};

const GRADE_ORDER = new Map<string, number>(
  [
    "nursery",
    "pre_k",
    "kindergarten",
    "grade_1",
    "grade_2",
    "grade_3",
    "grade_4",
    "grade_5",
    "grade_6",
    "grade_7",
    "grade_8",
  ].map((grade, index) => [grade, index]),
);

const RESOURCE_CARD_DESCRIPTORS: ResourceCardDescriptor[] = [
  {
    key: "parent_handbook",
    title: "Parent Handbook",
    subtitle: "Rules, policies and procedures",
    icon: ClipboardList,
    iconColor: "text-primary",
    iconBg: "bg-primary/10",
  },
  {
    key: "school_calendar",
    title: "School Calendar",
    subtitle: "Important dates and holidays",
    icon: Calendar,
    iconColor: "text-jungle",
    iconBg: "bg-jungle/10",
  },
  {
    key: "policy_documents",
    title: "Policy Documents",
    subtitle: "School board policies",
    icon: Info,
    iconColor: "text-irongrey",
    iconBg: "bg-irongrey/10",
  },
  {
    key: "help_support",
    title: "Help & Support",
    subtitle: "Contact the office",
    icon: CircleHelp,
    iconColor: "text-strawberry",
    iconBg: "bg-strawberry/10",
  },
  {
    key: "staff_directory",
    title: "Staff Directory",
    subtitle: "Faculty contacts",
    icon: Users,
    iconColor: "text-aquamarine",
    iconBg: "bg-aquamarine/10",
  },
];

const EmptyState = ({ children }: { children: React.ReactNode }) => (
  <p className="text-sm text-irongrey/50 font-medium">{children}</p>
);

const ErrorState = ({ children }: { children: React.ReactNode }) => (
  <SupportErrorMessage>{children}</SupportErrorMessage>
);

const asRecord = (value: unknown): Record<string, unknown> =>
  value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};

const parseRecord = (value: unknown): Record<string, unknown> => {
  if (typeof value === "string") {
    try {
      return asRecord(JSON.parse(value));
    } catch {
      return {};
    }
  }
  return asRecord(value);
};

const readText = (value: unknown): string => String(value ?? "").trim();

const parseBoolean = (value: unknown): boolean | undefined => {
  if (typeof value === "boolean") {
    return value;
  }
  if (typeof value === "number" && Number.isFinite(value)) {
    return value !== 0;
  }
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    if (!normalized) {
      return undefined;
    }
    if (["true", "1", "yes", "on"].includes(normalized)) {
      return true;
    }
    if (["false", "0", "no", "off"].includes(normalized)) {
      return false;
    }
  }
  return undefined;
};

const normalizeToken = (value: unknown): string =>
  readText(value)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");

const toTitleCase = (value: string): string =>
  value
    .split(/[\s_-]+/)
    .map((segment) => segment.trim())
    .filter(Boolean)
    .map(
      (segment) =>
        `${segment.slice(0, 1).toUpperCase()}${segment.slice(1).toLowerCase()}`,
    )
    .join(" ");

const toEpochMilliseconds = (value: unknown): number => {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value > 1_000_000_000_000 ? value : value * 1000;
  }

  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return 0;

    const numeric = Number(trimmed);
    if (Number.isFinite(numeric)) {
      return numeric > 1_000_000_000_000 ? numeric : numeric * 1000;
    }

    const parsed = Date.parse(trimmed);
    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }

  return 0;
};

const isSameDate = (leftMs: number, rightMs: number): boolean => {
  const left = new Date(leftMs);
  const right = new Date(rightMs);
  return (
    left.getFullYear() === right.getFullYear() &&
    left.getMonth() === right.getMonth() &&
    left.getDate() === right.getDate()
  );
};

const formatRelativeTime = (timestampMs: number): string => {
  if (!Number.isFinite(timestampMs) || timestampMs <= 0) {
    return "JUST NOW";
  }

  const nowMs = Date.now();
  const deltaMs = Math.max(0, nowMs - timestampMs);
  const deltaMinutes = Math.floor(deltaMs / 60_000);
  if (deltaMinutes < 1) return "JUST NOW";
  if (deltaMinutes < 60) return `${deltaMinutes}M AGO`;

  const deltaHours = Math.floor(deltaMinutes / 60);
  if (deltaHours < 24) return `${deltaHours}H AGO`;

  const yesterdayMs = nowMs - 24 * 60 * 60 * 1000;
  if (isSameDate(timestampMs, yesterdayMs)) return "YESTERDAY";

  const deltaDays = Math.max(1, Math.floor(deltaHours / 24));
  return `${deltaDays} DAYS AGO`;
};

const formatClockTime = (timestampMs: number): string => {
  if (!Number.isFinite(timestampMs) || timestampMs <= 0) {
    return "Time pending";
  }
  return new Date(timestampMs).toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
};

const formatGradeLabel = (rawGrade: string): string => {
  const token = normalizeToken(rawGrade);
  const gradeMatch = token.match(/^grade_?(\d+)$/);
  if (gradeMatch?.[1]) {
    return `${gradeMatch[1]} GRADE`;
  }
  if (/^\d+$/.test(token)) {
    return `${token} GRADE`;
  }
  if (token === "pre_k" || token === "prek") return "PRE-K";
  if (token === "kindergarten") return "KINDERGARTEN";
  if (token === "nursery") return "NURSERY";
  return token ? token.replace(/_/g, " ").toUpperCase() : "GRADE";
};

const resolveGradeSortIndex = (rawGrade: string): number => {
  const token = normalizeToken(rawGrade);
  const enumIndex = GRADE_ORDER.get(token);
  if (typeof enumIndex === "number") return enumIndex;

  const numericMatch = token.match(/^(\d+)$/);
  if (numericMatch?.[1]) {
    return 100 + Number(numericMatch[1]);
  }
  return Number.MAX_SAFE_INTEGER;
};

const isNotificationUnread = (record: Record<string, unknown>): boolean => {
  const deliveryState = normalizeToken(record.deliveryState ?? record.status);
  if (deliveryState) {
    return deliveryState !== "read" && deliveryState !== "archived";
  }
  if (record.readAt != null) {
    return false;
  }
  const actorState = asRecord(record.actorState);
  const unreadCount = Number(actorState.unreadCount ?? 0);
  if (Number.isFinite(unreadCount)) {
    return unreadCount > 0;
  }
  return true;
};

const deriveResourceCardKey = (
  record: Record<string, unknown>,
): string | null => {
  const tokens = [
    record.resourceId,
    record.resource_id,
    record.id,
    record.key,
    record.resourceKey,
    record.title,
    record.name,
  ]
    .map((value) => normalizeToken(value))
    .filter(Boolean);

  if (
    tokens.some(
      (token) =>
        token.includes("parent_handbook") || token.includes("handbook"),
    )
  ) {
    return "parent_handbook";
  }
  if (
    tokens.some(
      (token) =>
        token.includes("school_calendar") || token.includes("calendar"),
    )
  ) {
    return "school_calendar";
  }
  if (
    tokens.some(
      (token) => token.includes("policy_documents") || token.includes("policy"),
    )
  ) {
    return "policy_documents";
  }
  if (
    tokens.some(
      (token) =>
        token.includes("help_support") ||
        token.includes("help") ||
        token.includes("support"),
    )
  ) {
    return "help_support";
  }
  if (
    tokens.some(
      (token) =>
        token.includes("staff_directory") ||
        token.includes("staff") ||
        token.includes("directory"),
    )
  ) {
    return "staff_directory";
  }
  return null;
};

const resolveResourceHref = (record: Record<string, unknown>): string => {
  const ext = asRecord(record.ext);
  const candidates = [
    record.pdfUrl,
    record.pdf_url,
    record.url,
    record.downloadUrl,
    record.download_url,
    record.fileUrl,
    record.file_url,
    ext.pdfUrl,
    ext.pdf_url,
    ext.url,
    ext.downloadUrl,
    ext.download_url,
  ];

  for (const candidate of candidates) {
    const href = readText(candidate);
    if (href) {
      return href;
    }
  }

  return "";
};

const extractParnasSnapshot = (rawPayload: unknown): ParnasSnapshot | null => {
  const payload = parseRecord(rawPayload);
  const orgExt = asRecord(asRecord(payload.organization).ext);
  const candidates = [
    asRecord(payload.parnasHayom),
    asRecord(payload.parnas_hayom),
    asRecord(payload.parnas),
    asRecord(orgExt.parnasHayom),
    asRecord(orgExt.parnas_hayom),
    asRecord(orgExt.parnas),
  ];

  for (const candidate of candidates) {
    if (!Object.keys(candidate).length) {
      continue;
    }
    const sponsorFamilyName = readText(
      candidate.sponsorFamilyName ??
        candidate.sponsor_family_name ??
        candidate.familyName ??
        candidate.family_name,
    );
    const dedicationText = readText(
      candidate.dedicationText ??
        candidate.dedication_text ??
        candidate.dedication,
    );
    const sponsorUrl =
      readText(
        candidate.sponsorUrl ??
          candidate.sponsor_url ??
          candidate.url ??
          candidate.path,
      ) || "/parent/portal/parnes-hayom";

    const hebrewDateLabel = readText(
      candidate.hebrewDateLabel ??
        candidate.hebrew_date_label ??
        candidate.hebrewDate ??
        candidate.hebrew_date,
    );

    if (
      sponsorFamilyName ||
      dedicationText ||
      sponsorUrl !== "/parent/portal/parnes-hayom"
    ) {
      return { sponsorFamilyName, dedicationText, sponsorUrl, hebrewDateLabel };
    }
  }

  return null;
};

const resolveParnasVisibility = (rawPayload: unknown): boolean => {
  const payload = parseRecord(rawPayload);
  const orgExt = asRecord(asRecord(payload.organization).ext);
  const orgExtParnes = asRecord(orgExt.parnesHayom);
  const orgExtParnas = asRecord(orgExt.parnasHayom);
  const payloadParnes = asRecord(payload.parnesHayom);
  const payloadParnas = asRecord(payload.parnasHayom);
  const payloadParnesSnake = asRecord(payload.parnes_hayom);
  const payloadParnasSnake = asRecord(payload.parnas_hayom);

  const candidates = [
    orgExt.parnesHayomVisibleToParents,
    orgExt.parnasHayomVisibleToParents,
    orgExt.parnes_hayom_visible_to_parents,
    orgExt.parnas_hayom_visible_to_parents,
    orgExtParnes.visibleToParents,
    orgExtParnes.visible_to_parents,
    orgExtParnas.visibleToParents,
    orgExtParnas.visible_to_parents,
    payloadParnes.visibleToParents,
    payloadParnes.visible_to_parents,
    payloadParnas.visibleToParents,
    payloadParnas.visible_to_parents,
    payloadParnesSnake.visibleToParents,
    payloadParnesSnake.visible_to_parents,
    payloadParnasSnake.visibleToParents,
    payloadParnasSnake.visible_to_parents,
  ];

  for (const candidate of candidates) {
    const parsed = parseBoolean(candidate);
    if (typeof parsed === "boolean") {
      return parsed;
    }
  }

  return true;
};

const ResourceCardTile = ({ card }: { card: ResourceCard }) => {
  const Icon = card.icon;
  const body = (
    <article className="flex items-start gap-3">
      <span
        className={`w-10 h-10 rounded-xl flex items-center justify-center ${card.iconBg}`}
        aria-hidden
      >
        <Icon size={18} className={card.iconColor} />
      </span>
      <section className="min-w-0">
        <h3 className="text-sm font-bold text-irongrey">{card.title}</h3>
        <p className="text-xs text-irongrey/55 font-medium mt-1">
          {card.subtitle}
        </p>
      </section>
    </article>
  );

  if (!card.isAvailable) {
    return (
      <div
        className="rounded-2xl border border-gray-200 bg-gray-50/80 p-4 opacity-70 cursor-not-allowed"
        title="Planned release"
        aria-disabled="true"
      >
        {body}
        <p className="mt-3 text-[10px] font-bold uppercase tracking-widest text-irongrey/45">
          Planned release
        </p>
      </div>
    );
  }

  return (
    <a
      href={card.href}
      target="_blank"
      rel="noreferrer"
      className="block rounded-2xl border border-gray-100 bg-white p-4 transition-colors hover:bg-gray-50/70 focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary"
      data-testid="parent-portal-dashboard-resource-card"
    >
      {body}
    </a>
  );
};

export function PortalDashboardPage() {
  const { profile } = useShellProfile();
  const actorId = readText(profile?.userSub);

  const notificationsQuery = useListNotificationsQuery(
    actorId ? { actorId, includeRead: true, limit: 5 } : {},
    { skip: !actorId },
  );
  const [markAllNotificationsRead] = useMarkAllNotificationsReadMutation();
  const studentProfilesQuery = useListPortalStudentProfilesQuery(undefined, {
    skip: !actorId,
  });
  const calendarQuery = useListPortalCalendarEventsQuery(undefined, {
    skip: !actorId,
  });
  const resourcesQuery = useListPortalResourcesQuery(undefined, {
    skip: !actorId,
  });
  const settingsQuery = useGetSettingsModuleConfigQuery(
    { moduleKey: "staff_settings" },
    { skip: !actorId },
  );
  const settingsState = useMemo(
    () => parseSettingsState(asRecord(settingsQuery.data).payload),
    [settingsQuery.data],
  );
  const visibleParentNavIds = useMemo(() => {
    if (settingsQuery.isError || !settingsQuery.data) {
      return new Set<string>();
    }
    return resolveEffectiveParentPortalNavIds(
      resolveEnabledParentSidebarNavIds(
        materializeModuleGovernanceState(settingsState.governance),
      ),
    );
  }, [settingsQuery.data, settingsQuery.isError, settingsState.governance]);
  const isFinancialAidParentModuleVisible = useMemo(
    () => isParentPortalModuleVisible(visibleParentNavIds, "financial-aid"),
    [visibleParentNavIds],
  );
  const financialAidDashboardQueriesEnabled = isFinancialAidParentModuleVisible;
  const financialAidOpenCycle = useFinancialAidOpenCycle(
    undefined,
    financialAidDashboardQueriesEnabled,
  );
  const financialAidEffectiveAcademicYear = useMemo(
    () =>
      resolveFinancialAidOpenCycleAcademicYear(
        asRecord(financialAidOpenCycle.data),
      ),
    [financialAidOpenCycle.data],
  );
  const financialAidApplicationsQuery = useFinancialAidByApplicant(actorId, {
    enabled: Boolean(actorId && financialAidDashboardQueriesEnabled),
    academicYear: financialAidEffectiveAcademicYear,
  });

  const notifications = useMemo<DashboardNotification[]>(() => {
    const records = (notificationsQuery.data?.items ?? []).map((item) =>
      asRecord(item),
    );
    return records
      .map((record, index) => ({
        id: readText(record.id) || `notification-${index}`,
        message: readText(
          record.body ?? record.message ?? record.title ?? "Notification",
        ),
        unread: isNotificationUnread(record),
        createdAtMs: toEpochMilliseconds(
          record.createdAt ?? record.updatedAt ?? record.readAt,
        ),
      }))
      .sort((left, right) => right.createdAtMs - left.createdAtMs)
      .slice(0, 5);
  }, [notificationsQuery.data?.items]);

  const unreadNotificationIds = useMemo(
    () =>
      notifications.filter((entry) => entry.unread).map((entry) => entry.id),
    [notifications],
  );
  const autoReadAttemptRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!actorId || unreadNotificationIds.length === 0) {
      return;
    }

    const attemptKey = `${actorId}:${unreadNotificationIds.join("|")}`;
    if (autoReadAttemptRef.current.has(attemptKey)) {
      return;
    }
    autoReadAttemptRef.current.add(attemptKey);

    void markAllNotificationsRead({
      actorId,
      includeRead: true,
      readAt: Date.now(),
    })
      .unwrap()
      .catch(() => {
        // Keep dashboard render resilient if auto-read mutation fails.
      });
  }, [actorId, unreadNotificationIds, markAllNotificationsRead]);

  const scheduleByStudentId = useMemo(() => {
    const map = new Map<string, StudentScheduleEntry[]>();
    const events = (calendarQuery.data ?? []).map((item) => asRecord(item));
    const now = new Date();
    const dayStart = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate(),
    ).getTime();
    const dayEnd = dayStart + 24 * 60 * 60 * 1000;

    for (const event of events) {
      const studentId = readText(event.childApplicationId ?? event.studentId);
      if (!studentId) {
        continue;
      }
      const startAtMs = toEpochMilliseconds(
        event.startDateTime ?? event.startDate ?? event.date,
      );
      if (
        !Number.isFinite(startAtMs) ||
        startAtMs < dayStart ||
        startAtMs >= dayEnd
      ) {
        continue;
      }

      const subject =
        readText(event.subject ?? event.title) ||
        toTitleCase(readText(event.eventType)) ||
        "Scheduled event";

      const existing = map.get(studentId) ?? [];
      existing.push({
        id: `${studentId}-${existing.length}`,
        atMs: startAtMs,
        timeLabel: formatClockTime(startAtMs),
        subject,
      });
      map.set(studentId, existing);
    }

    for (const [studentId, entries] of map.entries()) {
      entries.sort((left, right) => left.atMs - right.atMs);
      map.set(studentId, entries);
    }

    return map;
  }, [calendarQuery.data]);

  const studentCards = useMemo<StudentCard[]>(() => {
    const records = (studentProfilesQuery.data?.items ?? []).map((item) =>
      asRecord(item),
    );

    const cards = records.map((record, index) => {
      const childInfo = asRecord(record.childInfo);
      const firstName = readText(childInfo.firstName) || "Student";
      const lastName = readText(childInfo.lastName);
      const gradeToken = readText(
        record.gradeApplyingFor ?? childInfo.currentGrade,
      );
      const id = readText(record.id ?? record.studentId) || `student-${index}`;

      const scheduleEntries = scheduleByStudentId.get(id) ?? [];
      return {
        id,
        firstName,
        lastName,
        gradeToken,
        gradeLabel: formatGradeLabel(gradeToken),
        avatarInitial: firstName.slice(0, 1).toUpperCase() || "S",
        scheduleEntries,
      };
    });

    return cards.sort((left, right) => {
      const gradeDelta =
        resolveGradeSortIndex(left.gradeToken) -
        resolveGradeSortIndex(right.gradeToken);
      if (gradeDelta !== 0) return gradeDelta;
      const firstNameDelta = left.firstName.localeCompare(right.firstName);
      if (firstNameDelta !== 0) return firstNameDelta;
      return left.lastName.localeCompare(right.lastName);
    });
  }, [studentProfilesQuery.data?.items, scheduleByStudentId]);

  const parnasSnapshot = useMemo(() => {
    const payload = asRecord(settingsQuery.data).payload;
    return extractParnasSnapshot(payload);
  }, [settingsQuery.data]);
  const isParnasHayomVisible = useMemo(() => {
    if (settingsQuery.isError || !settingsQuery.data) {
      return false;
    }
    const payload = asRecord(settingsQuery.data).payload;
    return resolveParnasVisibility(payload);
  }, [settingsQuery.data, settingsQuery.isError]);

  const parnasSponsorUrl =
    parnasSnapshot?.sponsorUrl || "/parent/portal/parnes-hayom";
  const hasParnasSponsor = Boolean(readText(parnasSnapshot?.sponsorFamilyName));

  const resourceCards = useMemo<ResourceCard[]>(() => {
    const keyedResources = new Map<string, Record<string, unknown>>();
    for (const resource of resourcesQuery.data ?? []) {
      const record = asRecord(resource);
      const key = deriveResourceCardKey(record);
      if (!key || keyedResources.has(key)) continue;
      keyedResources.set(key, record);
    }

    return RESOURCE_CARD_DESCRIPTORS.map((descriptor) => {
      const source = keyedResources.get(descriptor.key);
      const href = source ? resolveResourceHref(source) : "";
      return {
        ...descriptor,
        href,
        isAvailable: Boolean(href),
      };
    });
  }, [resourcesQuery.data]);

  const topResourceCards = resourceCards.slice(0, 3);
  const bottomResourceCards = resourceCards.slice(3);
  const studentGridClassName =
    studentCards.length <= 1
      ? "grid grid-cols-1 gap-4"
      : "grid grid-cols-1 md:grid-cols-2 gap-4";
  const financialAidDashboardSummary = useMemo(() => {
    const cycleLookupPending =
      financialAidOpenCycle.isLoading &&
      !financialAidOpenCycle.data &&
      !financialAidOpenCycle.error;
    const settingsLookupPending =
      settingsQuery.isLoading && !settingsQuery.data && !settingsQuery.error;
    if (cycleLookupPending || settingsLookupPending) return null;
    if (!isFinancialAidParentModuleVisible) return null;
    return resolveCurrentParentFinancialAidApplicationSummary(
      financialAidApplicationsQuery.data?.items,
      {
        applicantId: actorId,
        academicYear: financialAidEffectiveAcademicYear,
        includeFutureYears: true,
      },
    );
  }, [
    actorId,
    financialAidEffectiveAcademicYear,
    financialAidApplicationsQuery.data?.items,
    financialAidOpenCycle.data,
    financialAidOpenCycle.error,
    financialAidOpenCycle.isLoading,
    isFinancialAidParentModuleVisible,
    settingsQuery.data,
    settingsQuery.error,
    settingsQuery.isLoading,
  ]);

  return (
    <main
      data-testid="parent-portal-dashboard"
      data-extracted-from="owned:ystnj/parent-portal/dashboard"
      className="w-full space-y-6 pb-20"
    >
      {!actorId ? (
        <ErrorState>Unable to resolve your account identity.</ErrorState>
      ) : null}

      <section className="grid grid-cols-1 xl:grid-cols-5 gap-4 md:gap-6">
        <section
          data-testid="parent-portal-dashboard-notifications"
          className="xl:col-span-3 bg-white border border-gray-100 rounded-3xl shadow-card p-6"
          aria-label="Notifications"
        >
          <header className="flex items-start gap-3">
            <span
              className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center"
              aria-hidden
            >
              <Bell size={18} className="text-primary" />
            </span>
            <section>
              <h2 className="text-base font-bold text-irongrey">
                Notifications
              </h2>
              <p className="text-[10px] font-bold text-irongrey/45 uppercase tracking-widest mt-1">
                Stay up to date
              </p>
            </section>
          </header>

          <section className="mt-5">
            {notificationsQuery.isLoading ? (
              <ul className="divide-y divide-gray-100" aria-hidden>
                {Array.from({ length: 5 }).map((_, index) => (
                  <li
                    key={`dashboard-notification-skeleton-${index}`}
                    className="py-3"
                  >
                    <div className="animate-pulse flex items-center gap-3">
                      <span className="w-2 h-2 rounded-full bg-gray-200 shrink-0" />
                      <span className="h-3 bg-gray-100 rounded flex-1" />
                      <span className="h-2 w-16 bg-gray-100 rounded shrink-0" />
                    </div>
                  </li>
                ))}
              </ul>
            ) : null}

            {notificationsQuery.error ? (
              <ErrorState>Unable to load notifications right now.</ErrorState>
            ) : null}

            {!notificationsQuery.isLoading &&
            !notificationsQuery.error &&
            notifications.length === 0 ? (
              <EmptyState>No new notifications</EmptyState>
            ) : null}

            {!notificationsQuery.isLoading &&
            !notificationsQuery.error &&
            notifications.length > 0 ? (
              <ul className="divide-y divide-gray-100">
                {notifications.map((notification) => (
                  <li
                    key={notification.id}
                    className="py-3"
                    data-testid="parent-portal-dashboard-notification-row"
                  >
                    <section className="flex items-start gap-3">
                      {notification.unread ? (
                        <span
                          className="w-2 h-2 rounded-full bg-primary mt-[7px] shrink-0"
                          aria-hidden
                        />
                      ) : (
                        <span
                          className="w-2 h-2 mt-[7px] shrink-0"
                          aria-hidden
                        />
                      )}
                      <p className="min-w-0 flex-1 text-sm font-medium text-irongrey truncate">
                        {notification.message}
                      </p>
                      <time className="text-[10px] font-bold uppercase tracking-widest text-irongrey/45 whitespace-nowrap">
                        {formatRelativeTime(notification.createdAtMs)}
                      </time>
                    </section>
                  </li>
                ))}
              </ul>
            ) : null}
          </section>
        </section>

        <section
          data-testid="parent-portal-dashboard-parnas-hayom"
          className="xl:col-span-2 bg-amber-50/70 border border-amber-100 rounded-3xl shadow-card p-6 relative"
          style={isParnasHayomVisible ? undefined : { display: "none" }}
          aria-label="Parnas Hayom"
        >
          <header className="pr-10">
            <p className="text-[10px] font-bold text-amber-700/80 uppercase tracking-widest">
              Parnas Hayom
            </p>
            <span
              className="absolute right-6 top-6 w-9 h-9 rounded-full bg-amber-200/60 flex items-center justify-center"
              aria-hidden
            >
              <Heart size={16} className="text-amber-700" />
            </span>
          </header>

          <section className="mt-5">
            {hasParnasSponsor ? (
              <section className="space-y-3">
                <p className="text-sm font-semibold text-irongrey">
                  Sponsored by the {readText(parnasSnapshot?.sponsorFamilyName)}{" "}
                  Family
                </p>
                {readText(parnasSnapshot?.hebrewDateLabel) ? (
                  <p className="text-xs font-medium text-amber-700/60">
                    {readText(parnasSnapshot?.hebrewDateLabel)}
                  </p>
                ) : null}
                {readText(parnasSnapshot?.dedicationText) ? (
                  <p className="text-sm text-irongrey/75 italic leading-relaxed">
                    {readText(parnasSnapshot?.dedicationText)}
                  </p>
                ) : null}
              </section>
            ) : (
              <p className="text-sm font-medium text-irongrey/65">
                No sponsor for today
              </p>
            )}

            <Link
              to={parnasSponsorUrl}
              className="inline-flex items-center justify-center mt-5 px-4 py-2 rounded-xl bg-amber-200/70 text-amber-900 text-xs font-bold uppercase tracking-wider hover:bg-amber-200 transition-colors"
            >
              + Sponsor a day
            </Link>
          </section>
        </section>
      </section>

      {financialAidDashboardSummary ? (
        <section
          data-testid="parent-portal-dashboard-financial-aid-status"
          className="bg-white border border-gray-100 rounded-3xl shadow-card p-6"
          aria-label="Financial Aid application status"
        >
          <header className="flex items-start justify-between gap-4">
            <section>
              <p className="text-[10px] font-bold text-primary uppercase tracking-widest">
                Financial Aid
              </p>
              <h2 className="text-xl font-bold text-irongrey tracking-tight mt-1">
                Application status
              </h2>
            </section>
            <Link
              to="/parent/portal/financial-aid"
              className="inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-xl bg-primary/10 text-primary text-xs font-bold uppercase tracking-wider hover:bg-primary/15 transition-colors"
            >
              View
              <ChevronRight size={14} aria-hidden />
            </Link>
          </header>

          <section className="mt-5 grid grid-cols-1 md:grid-cols-3 gap-3">
            <article className="rounded-2xl border border-gray-100 bg-gray-50/70 p-4">
              <p className="text-[10px] font-bold uppercase tracking-widest text-irongrey/45">
                Status
              </p>
              <p
                data-testid="parent-portal-dashboard-financial-aid-status-value"
                className="text-base font-bold text-irongrey mt-2"
              >
                {financialAidDashboardSummary.statusLabel}
              </p>
              <p className="text-xs font-semibold text-irongrey/50 mt-1">
                {financialAidDashboardSummary.academicYear}
              </p>
            </article>

            <article className="rounded-2xl border border-gray-100 bg-gray-50/70 p-4">
              <p className="text-[10px] font-bold uppercase tracking-widest text-irongrey/45">
                Submitted
              </p>
              <p
                data-testid="parent-portal-dashboard-financial-aid-submitted-at"
                className="text-base font-bold text-irongrey mt-2"
              >
                {financialAidDashboardSummary.submittedDateLabel ??
                  "Not submitted yet"}
              </p>
            </article>

            <article className="rounded-2xl border border-gray-100 bg-gray-50/70 p-4">
              <p className="text-[10px] font-bold uppercase tracking-widest text-irongrey/45">
                Reference
              </p>
              <p
                data-testid="parent-portal-dashboard-financial-aid-reference"
                className="text-base font-bold text-irongrey mt-2 truncate"
                title={financialAidDashboardSummary.code}
              >
                {financialAidDashboardSummary.code}
              </p>
            </article>
          </section>
        </section>
      ) : null}

      <section
        data-testid="parent-portal-dashboard-students"
        className="bg-white border border-gray-100 rounded-3xl shadow-card p-6"
        aria-label="Your students and today's schedule"
      >
        <header className="mb-4">
          <h2 className="text-sm font-bold text-irongrey uppercase tracking-widest">
            Your Students
          </h2>
        </header>

        {studentProfilesQuery.isLoading ? (
          <EmptyState>Loading students...</EmptyState>
        ) : null}
        {studentProfilesQuery.error ? (
          <ErrorState>Unable to load student profiles right now.</ErrorState>
        ) : null}

        {!studentProfilesQuery.isLoading &&
        !studentProfilesQuery.error &&
        studentCards.length === 0 ? (
          <EmptyState>Student roster is syncing for this account.</EmptyState>
        ) : null}

        {!studentProfilesQuery.isLoading &&
        !studentProfilesQuery.error &&
        studentCards.length > 0 ? (
          <section className={studentGridClassName}>
            {studentCards.map((student) => (
              <article
                key={student.id}
                className="rounded-2xl border border-gray-100 bg-white p-4"
                data-testid="parent-portal-dashboard-student-card"
              >
                <header className="flex items-center justify-between gap-4">
                  <section className="flex items-center gap-3 min-w-0">
                    <span className="w-10 h-10 rounded-full bg-primary/10 text-primary text-sm font-bold inline-flex items-center justify-center shrink-0">
                      {student.avatarInitial}
                    </span>
                    <section className="min-w-0">
                      <h3 className="text-sm font-bold text-irongrey truncate">
                        {[student.firstName, student.lastName]
                          .filter(Boolean)
                          .join(" ")}
                      </h3>
                      <p className="text-[10px] font-bold uppercase tracking-widest text-irongrey/45 mt-1">
                        {student.gradeLabel}
                      </p>
                    </section>
                  </section>

                  <button
                    type="button"
                    disabled
                    title="Student profile routing is scheduled"
                    className="w-8 h-8 rounded-full border border-gray-200 text-irongrey/45 flex items-center justify-center cursor-not-allowed"
                    aria-label="Student profile routing is scheduled"
                  >
                    <ChevronRight size={16} />
                  </button>
                </header>

                <section className="mt-4">
                  <p className="flex items-center gap-2 text-[10px] font-bold text-irongrey/45 uppercase tracking-widest">
                    <Calendar size={12} aria-hidden />
                    Today&apos;s schedule
                  </p>

                  {student.scheduleEntries.length === 0 ? (
                    <p className="text-sm text-irongrey/55 font-medium mt-3">
                      No classes scheduled today
                    </p>
                  ) : (
                    <ul className="mt-3 divide-y divide-gray-100">
                      {student.scheduleEntries.map((entry) => (
                        <li
                          key={entry.id}
                          className="py-2.5 flex items-center justify-between gap-4"
                        >
                          <span className="text-xs font-bold text-irongrey/55 whitespace-nowrap">
                            {entry.timeLabel}
                          </span>
                          <span className="text-sm text-irongrey font-medium text-right">
                            {entry.subject}
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}

                  <footer className="mt-4 pt-4 border-t border-gray-100 text-center">
                    <Link
                      to="/parent/portal/calendar"
                      className="text-xs font-bold uppercase tracking-widest text-primary hover:text-primary/80 transition-colors"
                    >
                      View Calendar
                    </Link>
                  </footer>
                </section>
              </article>
            ))}
          </section>
        ) : null}
      </section>

      <section
        data-testid="parent-portal-dashboard-resources"
        className="bg-white border border-gray-100 rounded-3xl shadow-card p-6"
        aria-label="Parent resources"
      >
        <header className="mb-4">
          <h2 className="text-xl font-bold text-irongrey tracking-tight">
            Parent Resources
          </h2>
        </header>

        {resourcesQuery.isLoading ? (
          <p className="text-xs font-semibold uppercase tracking-widest text-irongrey/45 mb-4">
            Loading resource availability...
          </p>
        ) : null}
        {resourcesQuery.error ? (
          <ErrorState>Unable to load resource links right now.</ErrorState>
        ) : null}

        <section className="space-y-4">
          <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {topResourceCards.map((card) => (
              <ResourceCardTile key={card.key} card={card} />
            ))}
          </section>

          <section className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {bottomResourceCards.map((card) => (
              <ResourceCardTile key={card.key} card={card} />
            ))}
          </section>
        </section>
      </section>
    </main>
  );
}
